import api from '../../../lib/api';
import type {
  HelpdeskEscalationRoster,
  HelpdeskRosterCandidate,
  ReplaceHelpdeskRosterRequest,
} from '@workspace/types';
import { scopedParams } from './contactAgentScope';

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

function requireData<T>(response: { data: ApiResponse<T> }, fallbackError: string): T {
  if (!response.data.success || response.data.data === undefined) {
    throw new Error(response.data.error || fallbackError);
  }
  return response.data.data;
}

export type EscalationRosterApiBase = '/admin/mesa' | '/admin/contact-agent';

export function createEscalationRosterApi(basePath: EscalationRosterApiBase) {
  const agentPath = (agentId: string) => `${basePath}/agents/${agentId.slice(0, 64)}`;

  return {
    async getRoster(agentId: string): Promise<HelpdeskEscalationRoster> {
      const response = await api.get<ApiResponse<HelpdeskEscalationRoster>>(
        `${agentPath(agentId)}/roster`,
        { params: scopedParams() },
      );
      const data = requireData(response, 'No se pudo cargar el roster');
      return { ...data, agentId };
    },

    async putRoster(
      agentId: string,
      payload: ReplaceHelpdeskRosterRequest,
    ): Promise<HelpdeskEscalationRoster> {
      const response = await api.put<ApiResponse<HelpdeskEscalationRoster>>(
        `${agentPath(agentId)}/roster`,
        payload,
        { params: scopedParams() },
      );
      const data = requireData(response, 'No se pudo guardar el roster');
      return { ...data, agentId };
    },

    async listCandidates(agentId: string): Promise<HelpdeskRosterCandidate[]> {
      const response = await api.get<ApiResponse<HelpdeskRosterCandidate[]>>(
        `${agentPath(agentId)}/roster/candidates`,
        { params: scopedParams() },
      );
      return requireData(response, 'No se pudieron cargar los candidatos');
    },

    async testTelegramGroup(agentId: string, chatId: string): Promise<void> {
      const response = await api.post<ApiResponse<unknown>>(
        `${agentPath(agentId)}/roster/telegram-groups/test`,
        { chatId: chatId.slice(0, 20) },
        { params: scopedParams() },
      );
      if (!response.data.success) {
        throw new Error(response.data.error || 'No se pudo enviar el ping al grupo');
      }
    },
  };
}

export const mesaEscalationRosterApi = createEscalationRosterApi('/admin/mesa');
export const contactAgentEscalationRosterApi = createEscalationRosterApi('/admin/contact-agent');
